import { Code, LoadingOverlay, Modal, Space } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useParams } from 'react-router-dom';
import { SmallModal } from '../../../components/SmallModal';
import { Title } from '../../../components/Title';
import { FormButtons } from '../../../components/form/FormButtons';
import { mutate } from '../../../util/api/mutate';
import { useUpsertRearDisplayForm } from '../hooks/useUpsertRearDisplayForm';
import type {
	RearDisplayDto,
	UpsertRearDisplayDto,
} from '../types/RearDisplayDto';
import { UpsertRearDisplayForm } from './UpsertRearDisplayForm';

interface EditRearDisplayModalProps {
	defaultRearDisplay: UpsertRearDisplayDto;
	close: () => void;
	isOpened: boolean;
	triggerUpdateInList: (r: RearDisplayDto) => void;
}

export function EditRearDisplayModal({
	isOpened,
	close,
	triggerUpdateInList,
	defaultRearDisplay,
}: EditRearDisplayModalProps) {
	const { organisation } = useParams();

	const [
		showSuccessModal,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const { form, inputs } = useUpsertRearDisplayForm(defaultRearDisplay);

	const newRearDisplayMutation = mutate<UpsertRearDisplayDto, RearDisplayDto>(
		`${organisation}/rear-display/edit`,
		inputs,
		'multipart/form-data',
	);

	const resetAndClose = () => {
		form.reset();
		close();
		closeSuccessModal();
	};

	const complete = () => {
		if (newRearDisplayMutation.data?.value) {
			triggerUpdateInList(newRearDisplayMutation.data.value);
			form.setInitialValues(inputs);
		}

		resetAndClose();
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={resetAndClose}
				title={<Title label={'Edit Rear Display'} />}
				w={100}
				withCloseButton={true}
				size={'md'}
				centered
				translate="no"
			>
				<Code style={{ backgroundColor: 'transparent' }}>
					Ignore file upload to retain the existing advert
				</Code>
				<Space h={10} />
				<LoadingOverlay visible={newRearDisplayMutation.isLoading} />
				<SmallModal
					isOpen={showSuccessModal}
					close={complete}
					title={'Rear Display Edited!'}
					body={
						'A new Rear Display has been successfully edited. Click Ok to continue...'
					}
				/>
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () => {
						await newRearDisplayMutation.mutateAsync().then((value) => {
							if (value.isValid) {
								openSuccessModal();
							}
						});
					})}
				>
					<UpsertRearDisplayForm
						form={form}
						selectedBranchIds={inputs.branchIds}
					/>
					<FormButtons close={resetAndClose} />
				</form>
			</Modal>
		</>
	);
}
